/*
 * Created on Wed Oct 11 2023 4:56:55 PM
 *
 * ***************************************************************************************
 * Copyright (c) AVPS Technologies Inc 2023
 *
 * All Rights Reserved
 * Unpublished rights are reserved under the copyright laws of the United States.
 * The software contained in this media is proprietary to and embodies confidential
 * the technology of AVPS Technologies Inc. Possession, use, duplication or
 * dissemination of the software and media is authorized only pursuant to a valid
 * written license from AVPS Technologies Inc.
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * RESTRICTED RIGHTS LEGEND Use, duplication, or disclosure by the U.S. Government
 * is subject to restrictions as set forth in Subparagraph (c) (1) (ii) of DFARS
 * 252.227-7013 or in FAR 52.227-19, as applicable.
 ****************************************************************************************
 */

import React from 'react';
import { motion } from 'framer-motion';
import { FaSpinner, FaTimesCircle } from 'react-icons/fa';
import { getCleanProductName, getBookingDuration } from '../utils/orderUtils';

const CancelConfirmationDialog = ({
    show,
    order,
    onClose,
    onConfirm,
    refunding,
    formatDate,
    formatTime,
    getTimezoneAbbreviation,
    zoneId,
    isEducationDomain,
    isRetailDomain
}) => {

    if (!show || !order) return null;

    const productName = getCleanProductName(order);
    const categoryName = order?.categoryName;

    const bookingDuration = getBookingDuration(
        order,
        zoneId,
        formatDate,
        formatTime
    );

    const orderStatus = order?.currentStatus || order?.status;

    const isPaymentPending = [
        'PAYMENT_PENDING',
        'PAYMENT_PROCESSING'
    ].includes(orderStatus);

    const itemTypeLabel = isEducationDomain
        ? 'Enrollment'
        : isRetailDomain
            ? 'Order'
            : 'Booking';

    const confirmationMessage = isPaymentPending
        ? `This ${itemTypeLabel.toLowerCase()} will be cancelled. No refund will be processed.`
        : 'This will submit the cancellation and process any eligible refund through the original payment flow.';

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">

            <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="bg-white rounded-xl shadow-xl max-w-md w-full relative"
            >

                <button
                    onClick={onClose}
                    disabled={refunding}
                    className="absolute right-4 top-4 w-8 h-8 flex items-center justify-center rounded-full hover:bg-slate-100 transition-colors text-slate-500 hover:text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    <FaTimesCircle className="w-5 h-5" />
                </button>

                <div className="p-6">

                    {/* Header Section */}
                    <div className="mb-6">

                        <h3 className="text-xl font-semibold text-slate-900 mb-2">
                            Cancel {itemTypeLabel}?
                        </h3>

                        <div className="space-y-1">

                            {/* Main Message */}
                            <p className="text-sm text-slate-600">
                                {confirmationMessage}
                            </p>

                            {/* Secondary Message */}
                           

                        </div>

                    </div>

                    {/* Details Card */}
                    <div className="bg-slate-50 rounded-lg p-4 mb-6">

                        <div className="space-y-2">

                            <p className="text-slate-900 font-medium">
                                {productName}
                            </p>

                            {bookingDuration && !isEducationDomain && !isRetailDomain && (
                                <p className="text-sm text-slate-600">
                                    {bookingDuration.dateStr}
                                    {' • '}
                                    {bookingDuration.startTime}
                                    {' – '}
                                    {bookingDuration.endTime}
                                    {' '}
                                    ({getTimezoneAbbreviation(zoneId)})
                                </p>
                            )}

                            {isEducationDomain && (
                                <p className="text-sm text-slate-600">
                                    {categoryName && `${categoryName} • `}
                                    Enrolled on {formatDate(order.createdDate)}
                                </p>
                            )}

                            {isRetailDomain && (
                                <p className="text-sm text-slate-600">
                                    {categoryName && `${categoryName} • `}
                                    Order #{order.number || order.orderNumber}
                                </p>
                            )}

                        </div>

                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-3">

                        <button
                            onClick={onClose}
                            disabled={refunding}
                            className="flex-1 px-4 py-3 lg:py-2.5 bg-white border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Keep {itemTypeLabel}
                        </button>

                        <button
                            onClick={onConfirm}
                            disabled={refunding}
                            className="flex-1 px-4 py-3 lg:py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        >

                            {refunding ? (
                                <>
                                    <FaSpinner className="w-4 h-4 animate-spin" />
                                    Processing...
                                </>
                            ) : (
                                `Cancel ${itemTypeLabel}`
                            )}

                        </button>

                    </div>

                </div>

            </motion.div>

        </div>
    );
};

export default CancelConfirmationDialog;
